import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contabilidadService } from '../services/contabilidadService';
import { EGRESOS_PENDIENTES_QUERY_KEY } from './useEgresosPendientes';
import type { Agrupador, GuardarAgrupadorPayload, NombreItemAgrupable } from '../types';

export const AGRUPADORES_QUERY_KEY = ['contabilidad', 'agrupadores'] as const;
export const NOMBRES_AGRUPABLES_QUERY_KEY = [...AGRUPADORES_QUERY_KEY, 'nombres'] as const;

export function useAgrupadores() {
  const queryClient = useQueryClient();

  const agrupadoresQuery = useQuery<Agrupador[]>({
    queryKey: AGRUPADORES_QUERY_KEY,
    queryFn: () => contabilidadService.listAgrupadores(),
  });

  const nombresQuery = useQuery<NombreItemAgrupable[]>({
    queryKey: NOMBRES_AGRUPABLES_QUERY_KEY,
    queryFn: () => contabilidadService.listNombresAgrupables(),
  });

  // Cualquier cambio en el árbol o en las asociaciones cambia cómo se agrupan los egresos.
  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: AGRUPADORES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: EGRESOS_PENDIENTES_QUERY_KEY });
  };

  const createMutation = useMutation({
    mutationFn: (payload: GuardarAgrupadorPayload) => contabilidadService.createAgrupador(payload),
    onSuccess: invalidar,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: GuardarAgrupadorPayload }) =>
      contabilidadService.updateAgrupador(id, payload),
    onSuccess: invalidar,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => contabilidadService.deleteAgrupador(id),
    onSuccess: invalidar,
  });

  const asignarMutation = useMutation({
    mutationFn: ({ nombres, agrupadorId }: { nombres: string[]; agrupadorId: number | null }) =>
      contabilidadService.asignarNombres(nombres, agrupadorId),
    onSuccess: invalidar,
  });

  return {
    agrupadores: agrupadoresQuery.data ?? [],
    nombres: nombresQuery.data ?? [],
    isLoading: agrupadoresQuery.isLoading || nombresQuery.isLoading,
    createAgrupador: createMutation.mutateAsync,
    updateAgrupador: updateMutation.mutateAsync,
    deleteAgrupador: deleteMutation.mutateAsync,
    asignarNombres: asignarMutation.mutateAsync,
    isMutating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    isAsignando: asignarMutation.isPending,
  };
}
